// src/components/VaccineEstimateList.js
import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Spinner,
  Text,
  Input,
  Heading,
  InputGroup,
  InputLeftElement,
  Select,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines, faSearch } from "@fortawesome/free-solid-svg-icons";
import DataTable from "./AppDataTable";
import apiService from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import * as IconButton from "../themes/IconButtonSet";

// Custom styles for DataTable
const customStyles = {
  header: {
    style: {
      backgroundColor: "#6BA9B4",
      color: "white",
      fontSize: "18px",
      fontWeight: "bold",
    },
  },
  headCells: {
    style: {
      backgroundColor: "#6BA9B4",
      color: "white",
      fontSize: "16px",
      fontWeight: "semibold",
      padding: "10px",
    },
  },
  cells: {
    style: {
      padding: "10px",
      fontSize: "14px",
    },
  },
  rows: {
    style: {
      "&:hover": {
        backgroundColor: "#f5f5f5",
      },
    },
  },
};

// ฟังก์ชันกำหนดสไตล์สถานะ
const getStatusStyle = (status) => {
  switch (status) {
    case "Pending":
      return { bg: "#FFF3E0", color: "#D97706" };
    case "Confirmed":
      return { bg: "#D1FAE5", color: "#047857" };
    case "Completed":
      return { bg: "#E5E7EB", color: "#374151" };
    case "Cancelled":
      return { bg: "#FEE2E2", color: "#991B1B" };
    default:
      return { bg: "#EDF2F7", color: "#4A5568" };
  }
};

const VaccineEstimateList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId } = useAuth();
  const [estimates, setEstimates] = useState([]);
  const [filteredEstimates, setFilteredEstimates] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchYear, setSearchYear] = useState("");
  const [searchUnit, setSearchUnit] = useState("");

  const unitOptions = [
    "กลุ่มงานโคเนื้อ",
    "กลุ่มงานโคนม",
    "กลุ่มงานสัตว์ปีก",
    "กลุ่มงานสุกร",
  ];

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const columns = [
    {
      name: "เลขที่เอกสาร",
      selector: (row) => row.EstimateID,
      sortable: true,
    },
    {
      name: "วันที่เอกสาร",
      selector: (row) => new Date(row.DocumentDate).toLocaleString(),
      sortable: true,
    },
    {
      name: "ปีงบประมาณ",
      selector: (row) => row.EstimateYear,
      sortable: true,
    },
    {
      name: "กลุ่มงาน",
      selector: (row) => row.EstimateUnit,
      sortable: true,
    },
    {
      name: "ส่งมอบไปที่",
      selector: (row) => row.DeliveryToName,
      sortable: true,
    },
    {
      name: "สถานะ",
      selector: (row) => (
        <Box
          bg={getStatusStyle(row.DocumentStatus).bg}
          color={getStatusStyle(row.DocumentStatus).color}
          borderColor={getStatusStyle(row.DocumentStatus).color}
          borderWidth={1}
          borderRadius="full"
          px={3}
          py={1}
          textAlign="center"
          fontSize="sm"
          fontWeight="semibold"
          display="inline-block"
        >
          {row.DocumentStatus}
        </Box>
      ),
      sortable: true,
      width: "150px",
    },
    {
      name: "การดำเนินการ",
      cell: (row) => (
        <IconButton.View
          icon={faFileLines}
          onClick={() => navigate(`/vaccineestimate/${row.EstimateID}`)}
          label="ดูรายละเอียด"
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "120px",
    },
  ];

  const fetchWarehouses = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineWarehousesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setWarehouses(response.VaccineWarehousesList || []);
      }
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    }
  };

  const fetchEstimates = async () => {
    setIsLoading(true);
    try {
      await fetchWarehouses();
      const response = await apiService({
        method: "POST",
        url: "/getVaccineEstimatesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const data = response.VaccineEstimatesList || [];
        setEstimates(data);
        setFilteredEstimates(data);
      }
    } catch (error) {
      console.error("Error fetching estimates:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchEstimates();
    }
  }, [sessionId]);

  const handleSearch = () => {
    if (!searchYear && !searchUnit) {
      setFilteredEstimates(estimates);
      return;
    }

    const filtered = estimates.filter((row) => {
      const yearMatch = searchYear
        ? row.EstimateYear.toLowerCase().includes(searchYear.toLowerCase())
        : true;
      const unitMatch = searchUnit
        ? row.EstimateUnit === searchUnit
        : true;
      return yearMatch && unitMatch;
    });
    setFilteredEstimates(filtered);
  };

  const handleYearChange = (e) => {
    setSearchYear(e.target.value);
  };

  const handleUnitChange = (e) => {
    setSearchUnit(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchYear("");
    setSearchUnit("");
    setFilteredEstimates(estimates);
  };

  const handleCreateNew = () => {
    navigate("/vaccineestimate/new");
  };

  if (!sessionId) {
    return null;
  }

  return (
    <Box
      p={{ base: 4, md: 10, lg: 35 }}
      m={{ base: 2, md: 10, lg: 25 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <VStack spacing={6} align="start">
        <Box
          mb={5}
          borderRadius="full"
          w="100%"
          bgColor="iBlue.600"
          color="white"
          p={2}
        >
          <HStack spacing={5} ml={5}>
            <FontAwesomeIcon icon={faFileLines} color="white" size="xl" />
            <Heading
              as="h1"
              size={{ base: "md", md: "lg" }}
              fontWeight="normal"
            >
              รายการประเมินความต้องการวัคซีน
            </Heading>
          </HStack>
        </Box>
        <HStack
          w="100%"
          justify="space-between"
          flexWrap={{ base: "wrap", md: "nowrap" }}
          spacing={4}
        >
          {estimates.length > 0 && (
            <HStack
              w={{ base: "100%", md: "70%" }}
              spacing={4}
              flexWrap={{ base: "wrap", md: "nowrap" }}
            >
              <InputGroup w={{ base: "100%", md: "50%" }}>
                <InputLeftElement pointerEvents="none">
                  <FontAwesomeIcon icon={faSearch} color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="ค้นหาปีงบประมาณ..."
                  value={searchYear}
                  onChange={handleYearChange}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                  colorScheme="iBlue"
                  mt={1}
                />
              </InputGroup>
              <Select
                w={{ base: "100%", md: "50%" }}
                placeholder="เลือกกลุ่มงาน"
                value={searchUnit}
                onChange={handleUnitChange}
                onBlur={handleSearch}
                colorScheme="iBlue"
                mt={{ base: 4, md: 1 }}
              >
                {unitOptions.map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </Select>
              <IconButton.Clear onClick={handleClearSearch} mt={{ base: 4, md: 0 }} />
            </HStack>
          )}
          <IconButton.Add onClick={handleCreateNew} label="เพิ่มรายการใหม่" mt={{ base: 4, md: 0 }} />
        </HStack>
        {isLoading ? (
          <Spinner size="md" />
        ) : estimates.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ยังไม่มีข้อมูลใบประเมินการใช้วัคซีน
          </Text>
        ) : filteredEstimates.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ไม่พบข้อมูลที่ค้นหา
          </Text>
        ) : (
          <Box w="100%" overflowX="auto">
            <DataTable
              columns={columns}
              data={filteredEstimates}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={customStyles}
            />
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default VaccineEstimateList;